/**
 * @author       hugo@zumper.com (Hugo Pineda)
 * @copyright    Copyright (c) 2020, Zumper
 */

import { useMount } from '@zcode-ui/hook'
import React, { useRef, useState } from 'react'

import loadingImg from '../../img/loading.jpg'

import * as styles from './styles'

interface Props {
  alt?: string
  children?: React.ReactElement | React.ReactNode
  className?: string
  height: number
  imgTestId?: string
  lat: number
  loadingTestId?: string
  lng: number
  mapboxStyle?: string
  mapboxUsername?: string
  mapboxToken: string
  width: number
  zoom?: number
}

let showLoadingTimeout: NodeJS.Timeout

export const StaticMap = ({
  alt = '',
  children,
  className,
  height,
  imgTestId,
  lat,
  loadingTestId,
  lng,
  mapboxStyle = 'streets-v11',
  mapboxUsername = 'mapbox',
  mapboxToken,
  width,
  zoom = 15,
}: Props) => {
  const [loaded, setLoaded] = useState(false)
  const [showLoading, setShowLoading] = useState(true)
  const imgRef = useRef<HTMLImageElement>(null)

  useMount(() => {
    if (imgRef.current) {
      imgRef.current.onload = () => {
        setLoaded(true)
        // 300ms is the transition time from loading image to real image
        showLoadingTimeout = setTimeout(() => setShowLoading(false), 300)
      }
      imgRef.current.src = `https://api.mapbox.com/styles/v1/${mapboxUsername}/${mapboxStyle}/static/${lng},${lat},${zoom}/${width}x${height}@2x?access_token=${mapboxToken}`
    }
    return () => {
      clearTimeout(showLoadingTimeout)
    }
  })

  return (
    <div className={className} css={styles.staticMap}>
      <img
        alt={alt}
        css={[styles.image, loaded && styles.imageLoaded]}
        data-testid={imgTestId}
        height={height}
        loading="lazy"
        ref={imgRef}
        style={{ flexBasis: width }}
        width={width}
      />
      {children && <div css={styles.children}>{children}</div>}
      {showLoading && (
        <img
          alt="loading map"
          css={[styles.loading, loaded && styles.loadingDone]}
          data-testid={loadingTestId}
          height={height}
          loading="lazy"
          src={loadingImg}
          width={width}
        />
      )}
    </div>
  )
}
